import { useEffect, useRef } from 'react'

import { localStorageKeys } from '@/shared/config'

// Identifies this browser tab on the form-sync channel. Sent as the `x-form-sync-client`
// header with every save (see the public-forms route interceptor) and echoed back in the
// published event, so this tab can ignore its own writes instead of clobbering what the
// user typed since the save.
export const FORM_SYNC_CLIENT_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`

// Mirror of the backend's FormSyncEvent (backend/src/form-sync/form-sync.types.ts).
export interface FormSyncAnswersUpdatedEvent {
  type: 'answers-updated'
  verificationId: string
  formId: string
  page: number
  answersData: Record<string, unknown>
  sourceClientId?: string
  updatedAt: number
}

// Application close codes set by the backend gateway — retrying would fail the same way.
const TERMINAL_CLOSE_CODES = new Set([4400, 4401])

const RECONNECT_BASE_DELAY_MS = 500
const RECONNECT_MAX_DELAY_MS = 15000

const buildWsUrl = (formId: string, verificationId: string, token: string): string => {
  // Native WebSocket cannot set headers, so the token travels as a query param — same
  // exposure as the verification link itself, which already carries it.
  const base = (import.meta.env.PUBLIC_API_URL as string).replace(/^http/, 'ws')
  const params = new URLSearchParams({ formId, verificationId, token })
  return `${base}/ws/forms?${params.toString()}`
}

// The verification-link flow stores the token per verification id; same source as the
// public-forms axios interceptor.
const readUserClientToken = (verificationId: string): string | null => {
  try {
    const data = localStorage.getItem(localStorageKeys.verificationId(verificationId))
    if (!data) return null
    const parsed = JSON.parse(data) as { xUserClientToken?: string }
    return parsed.xUserClientToken ?? null
  } catch {
    return null
  }
}

interface UseFormSyncArgs {
  formId: string
  verificationId: string
  onAnswersUpdated: (event: FormSyncAnswersUpdatedEvent) => void
}

// Subscribes to the backend's form-sync WebSocket channel for this form + verification and
// invokes the handler for every answers update written by someone else (another tab, or the
// user-client agent saving through chat). Reconnects with capped backoff on connection loss.
export const useFormSync = ({ formId, verificationId, onAnswersUpdated }: UseFormSyncArgs) => {
  // Latest-handler ref so the socket effect doesn't reconnect every time the caller passes
  // a freshly-created callback.
  const handlerRef = useRef(onAnswersUpdated)
  useEffect(() => {
    handlerRef.current = onAnswersUpdated
  }, [onAnswersUpdated])

  useEffect(() => {
    if (!formId || !verificationId) return
    const token = readUserClientToken(verificationId)
    if (!token) return

    let socket: WebSocket | null = null
    let reconnectTimer: number | null = null
    let attempts = 0
    let disposed = false

    const connect = () => {
      if (disposed) return
      socket = new WebSocket(buildWsUrl(formId, verificationId, token))

      socket.onopen = () => {
        attempts = 0
      }

      socket.onmessage = (event: MessageEvent<string>) => {
        let message: FormSyncAnswersUpdatedEvent
        try {
          message = JSON.parse(event.data) as FormSyncAnswersUpdatedEvent
        } catch {
          return
        }
        if (message?.type !== 'answers-updated') return
        // Skip the echo of this tab's own saves.
        if (message.sourceClientId && message.sourceClientId === FORM_SYNC_CLIENT_ID) return
        handlerRef.current(message)
      }

      socket.onclose = (event: CloseEvent) => {
        if (disposed || TERMINAL_CLOSE_CODES.has(event.code)) return
        const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempts, RECONNECT_MAX_DELAY_MS)
        attempts += 1
        reconnectTimer = window.setTimeout(connect, delay)
      }
    }

    connect()

    return () => {
      disposed = true
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer)
      socket?.close()
    }
  }, [formId, verificationId])
}
